// Theatre lite (docs/plans/phase2-theatre.md §3.3).
import { queryOptions } from '@tanstack/react-query';
import {
  adminTheatreSchema,
  bookableProceduresSchema,
  cancelTheatreChargeResultSchema,
  checklistItemSchema,
  serviceItemSearchResultSchema,
  surgeonListSchema,
  theatreCaseSchema,
  theatreListSchema,
  visitTheatreCasesSchema,
  type bookCaseBodySchema,
  type ChecklistPhase,
  type createChecklistItemBodySchema,
  type createTheatreBodySchema,
  type rescheduleCaseBodySchema,
  type RecordChecklistBody,
  type updateChecklistItemBodySchema,
  type updateTheatreBodySchema,
} from '@wodi/shared';
import { z } from 'zod';

import { api } from './client.ts';

type In<T extends z.ZodType> = z.input<T>;

/** One day of the list; refreshes every minute. */
export const theatreListQuery = (date: string | null) =>
  queryOptions({
    queryKey: ['theatre', 'list', date ?? 'today'],
    queryFn: () =>
      api('GET', date === null ? '/theatre/list' : `/theatre/list?date=${date}`, {
        schema: theatreListSchema,
      }),
    refetchInterval: 60_000,
  });

export const theatreCaseQuery = (caseId: string) =>
  queryOptions({
    queryKey: ['theatre', 'case', caseId],
    queryFn: () => api('GET', `/theatre/cases/${caseId}`, { schema: theatreCaseSchema }),
  });

export const visitTheatreCasesQuery = (visitId: string) =>
  queryOptions({
    queryKey: ['theatre', 'visit', visitId],
    queryFn: () =>
      api('GET', `/visits/${visitId}/theatre-cases`, { schema: visitTheatreCasesSchema }),
  });

export const bookableProceduresQuery = (visitId: string) =>
  queryOptions({
    queryKey: ['theatre', 'bookable', visitId],
    queryFn: () =>
      api('GET', `/visits/${visitId}/theatre-procedures`, { schema: bookableProceduresSchema }),
  });

export const surgeonsQuery = queryOptions({
  queryKey: ['theatre', 'surgeons'],
  queryFn: () => api('GET', '/theatre/surgeons', { schema: surgeonListSchema }),
});

export const searchTheatreItems = (
  visitId: string,
  purpose: 'procedure' | 'service',
  query: string,
) =>
  api('POST', '/theatre/search-items', {
    body: { visitId, purpose, query },
    schema: serviceItemSearchResultSchema,
  });

export const bookCase = (body: In<typeof bookCaseBodySchema>) =>
  api('POST', '/theatre/cases', { body, schema: theatreCaseSchema });

export const rescheduleCase = (caseId: string, body: In<typeof rescheduleCaseBodySchema>) =>
  api('POST', `/theatre/cases/${caseId}/reschedule`, { body, schema: theatreCaseSchema });

export const cancelCase = (
  caseId: string,
  body: { reason: string; cancelProcedureRequest: boolean },
) =>
  api('POST', `/theatre/cases/${caseId}/cancel`, {
    body,
    schema: z.object({
      case: theatreCaseSchema,
      procedureCharge: z.enum(['cancelled', 'needs_credit_note', 'nothing_to_cancel']).nullable(),
    }),
  });

export const recordChecklist = (caseId: string, phase: ChecklistPhase, body: RecordChecklistBody) =>
  api('POST', `/theatre/cases/${caseId}/checklists/${phase}`, { body, schema: theatreCaseSchema });

export const signOperationNote = (caseId: string, sections: { key: string; text: string }[]) =>
  api('POST', `/theatre/cases/${caseId}/operation-note`, {
    body: { sections },
    schema: theatreCaseSchema,
  });

export const amendOperationNote = (
  caseId: string,
  body: { sections: { key: string; text: string }[]; reason: string },
) =>
  api('POST', `/theatre/cases/${caseId}/operation-note/amendments`, {
    body,
    schema: theatreCaseSchema,
  });

export const recordTheatreCharge = (
  caseId: string,
  body: { clientRequestId: string; serviceItemId: string; quantity: number },
) => api('POST', `/theatre/cases/${caseId}/charges`, { body, schema: theatreCaseSchema });

export const cancelTheatreCharge = (chargeId: string, reason: string) =>
  api('POST', `/theatre/charges/${chargeId}/cancel`, {
    body: { reason },
    schema: cancelTheatreChargeResultSchema,
  });

// --- Settings > Theatres ----------------------------------------------------------------------

export const adminTheatresQuery = queryOptions({
  queryKey: ['admin', 'theatres'],
  queryFn: () => api('GET', '/admin/theatres', { schema: z.array(adminTheatreSchema) }),
});

export const checklistItemsQuery = queryOptions({
  queryKey: ['admin', 'theatre-checklist-items'],
  queryFn: () =>
    api('GET', '/admin/theatre-checklist-items', { schema: z.array(checklistItemSchema) }),
});

export const createTheatre = (body: In<typeof createTheatreBodySchema>) =>
  api('POST', '/admin/theatres', { body, schema: adminTheatreSchema });

export const updateTheatre = (theatreId: string, body: In<typeof updateTheatreBodySchema>) =>
  api('PATCH', `/admin/theatres/${theatreId}`, { body, schema: adminTheatreSchema });

export const createChecklistItem = (body: In<typeof createChecklistItemBodySchema>) =>
  api('POST', '/admin/theatre-checklist-items', { body, schema: checklistItemSchema });

export const updateChecklistItem = (
  itemId: string,
  body: In<typeof updateChecklistItemBodySchema>,
) =>
  api('PATCH', `/admin/theatre-checklist-items/${itemId}`, { body, schema: checklistItemSchema });
